import { useEmailTimeZone } from "../useEmailTimeZone";
import { ApolloError } from "@apollo/client";
import React, { useCallback, useMemo, useRef } from "react";
import {
  useNavigate,
  useParams,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import { RoutePaths } from "../../../../enumStrings";
import {
  CmpNewsletterStatusEnum,
  useArchiveCmpNewsletterRecipientGroupMutation,
  useCreateCmpNewsletterMutation,
  useGetCmpManageMembersQuery,
  useGetCmpNewsletterDetailQuery,
  useGetCmpNewsletterRecipientGroupsQuery,
  usePreviewCmpNewsletterRecipientsLazyQuery,
  usePublishCmpNewsletterToNewsroomMutation,
  useSendCmpNewsletterNowMutation,
  useUpdateCmpNewsletterMutation,
  useUpdateCmpNewsletterNewsroomMutation,
} from "../../../../graphql/types";
import { useCmpEmailSettings } from "../../../../hooks/useCmpSettingsSection";
import useGetChamberFromUserIdInAuth from "../../../../hooks/useGetChamberFromUserIdInAuth";
import { useCmpOrganizationNoun } from "../../../../contexts/CmpOrganizationNounContext";
import {
  EMAIL_CHAMBER_LOADING_MESSAGE,
  EMAIL_OPERATION_ERROR_CONTEXT,
  assertEmailBuilderDraftPersisted,
  buildEmailDuplicateDraft,
  emailFormStateToInput,
  emailLoadErrorText,
  newsletterReturnToFormState,
  previewQueryToSummary,
  recipientGroupReturnToUiGroup,
} from "../emailBackendMappers";
import EmailForm from "../EmailForm";
import { useNewsletterDeliveryActions } from "../useNewsletterDeliveryActions";
import { useUnpublishNewsletterFromNewsroom } from "../useUnpublishNewsletterFromNewsroom";
import {
  getEnabledNewsletterSenderEmails,
  reconcileNewsletterSenderEmail,
} from "../emailSenderSelection";
import {
  EmailFormState,
  EmailAudienceMemberOption,
  buildDefaultCreateEmailForm,
} from "../emailMockData";
import { applyCmpOrganizationNoun } from "../../../../utils/cmpOrganizationNoun";
import { UserFacingError, buildStaffErrorFallback } from "../../../../utils/userFacingError";

const isNewsletterNotFoundError = (error?: ApolloError): boolean => {
  if (error?.networkError || error?.graphQLErrors?.length !== 1) return false;

  // Route by code first; the exact text stays as a fallback.
  return error.graphQLErrors[0].extensions?.cmpErrorReason === "CMP_NEWSLETTER_NOT_FOUND" ||
    error.graphQLErrors[0].message === "Newsletter not found";
};

const UpdateEmail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { emailId } = useParams<{ emailId: string }>();
  const noun = useCmpOrganizationNoun();
  const chamber = useGetChamberFromUserIdInAuth();
  const chamberId = chamber?.id || "";
  const { timeZone, loading: timeZoneLoading, error: timeZoneError, refetch: refetchTimeZone } = useEmailTimeZone(chamberId);
  const loadedEmailIdentity = useRef("");
  const emailIdentity = `${chamberId}:${emailId || ""}`;
  const handleSendSuccess = useCallback(() => {
    navigate(RoutePaths.chamberAdmin_emails_manage, { replace: true });
  }, [navigate]);

  const {
    data: detailData,
    previousData: previousDetailData,
    error: detailError,
    loading: detailLoading,
    refetch: refetchEmailDetail,
  } = useGetCmpNewsletterDetailQuery({
    variables: { input: { chamberId, newsletterId: emailId || "" } },
    skip: !chamberId || !emailId,
    fetchPolicy: "cache-and-network",
    context: EMAIL_OPERATION_ERROR_CONTEXT,
  });
  const {
    data: settingsData,
    error: settingsError,
    loading: settingsLoading,
    refetch: refetchSettings,
  } = useCmpEmailSettings(chamberId, "network-only", EMAIL_OPERATION_ERROR_CONTEXT);
  const currentEmailSettings = settingsData?.getChamberMemberProSettings.email;
  const senderEmailOptions = useMemo(
    () => getEnabledNewsletterSenderEmails(currentEmailSettings || {}),
    [currentEmailSettings]
  );
  const { data: groupsData, error: groupsError, refetch: refetchGroups } =
    useGetCmpNewsletterRecipientGroupsQuery({
      variables: { input: { chamberId, includeArchived: false } },
      skip: !chamberId,
      fetchPolicy: "cache-and-network",
      context: EMAIL_OPERATION_ERROR_CONTEXT,
    });
  const { data: membersData, error: membersError, loading: membersLoading, refetch: refetchMembers } = useGetCmpManageMembersQuery({
    // The audience picker needs member rows only (no invoice read).
    variables: { input: { chamberId, includeInvoiceSummary: false } },
    skip: !chamberId,
    fetchPolicy: "cache-and-network",
    context: EMAIL_OPERATION_ERROR_CONTEXT,
  });
  const [updateCmpNewsletter, { loading: updatingNewsletter }] =
    useUpdateCmpNewsletterMutation({ context: EMAIL_OPERATION_ERROR_CONTEXT });
  const [createCmpNewsletter] = useCreateCmpNewsletterMutation({ context: EMAIL_OPERATION_ERROR_CONTEXT });
  const [archiveCmpNewsletterRecipientGroup] =
    useArchiveCmpNewsletterRecipientGroupMutation({ context: EMAIL_OPERATION_ERROR_CONTEXT });
  const [previewCmpNewsletterRecipients] =
    usePreviewCmpNewsletterRecipientsLazyQuery({
      fetchPolicy: "network-only",
      context: EMAIL_OPERATION_ERROR_CONTEXT,
    });
  const [publishCmpNewsletterToNewsroom] =
    usePublishCmpNewsletterToNewsroomMutation({ context: EMAIL_OPERATION_ERROR_CONTEXT });
  const { unpublishFromNewsroom } = useUnpublishNewsletterFromNewsroom(chamberId);
  const [updateCmpNewsletterNewsroom] =
    useUpdateCmpNewsletterNewsroomMutation({ context: EMAIL_OPERATION_ERROR_CONTEXT });
  const [sendCmpNewsletterNow, { loading: sendingNow }] =
    useSendCmpNewsletterNowMutation({ context: EMAIL_OPERATION_ERROR_CONTEXT });

  const previousBackendEmail = previousDetailData?.getCmpNewsletterDetail.newsletter;
  // A refresh failure must retain this draft without reusing a different route's data.
  const backendEmail = detailData?.getCmpNewsletterDetail.newsletter || (
    (detailError || detailLoading) &&
    previousBackendEmail?.chamberId === chamberId &&
    previousBackendEmail?.id === emailId
      ? previousBackendEmail
      : undefined
  );
  const needsScheduleTimeZone = !!backendEmail?.scheduledAtUtcMilli;
  if (backendEmail && currentEmailSettings && !settingsLoading && !settingsError && (!needsScheduleTimeZone || timeZone)) {
    loadedEmailIdentity.current = emailIdentity;
  }
  // Settings refreshes cannot replace a loaded form with the initial loading screen.
  const hasLoadedEmail = loadedEmailIdentity.current === emailIdentity;
  const initialEmail = useMemo(() => {
    const loadedEmail = backendEmail
      ? newsletterReturnToFormState(backendEmail, timeZone || "")
      : buildDefaultCreateEmailForm({ chamberName: chamber?.name });
    const senderCanStillBeChanged =
      loadedEmail.status !== "sent" && loadedEmail.status !== "sending";

    return {
      ...loadedEmail,
      fromEmail: senderCanStillBeChanged
        ? reconcileNewsletterSenderEmail({
            currentSenderEmail: loadedEmail.fromEmail,
            enabledSenderEmails: senderEmailOptions,
          })
        : loadedEmail.fromEmail,
    };
  }, [backendEmail, chamber?.name, senderEmailOptions, timeZone]);
  const recipientGroups = useMemo(
    () =>
      (groupsData?.getCmpNewsletterRecipientGroups.groups || [])
        .filter((group) => !group.isArchived)
        .map(recipientGroupReturnToUiGroup),
    [groupsData?.getCmpNewsletterRecipientGroups.groups]
  );
  const memberOptions: EmailAudienceMemberOption[] = useMemo(
    () =>
      (membersData?.getCmpManageMembers.rows || []).map((member) => ({
        id: member.id,
        companyName: member.companyName,
        companyLogo: member.companyLogo,
        categoryLabels: member.categoryLabels,
        lifecycleStatus: member.lifecycleStatus,
        membershipTypeId: member.membershipTypeId || "",
        membershipTypeName: member.membershipTypeName || "",
        dateJoinedUtcMilli: member.dateJoinedUtcMilli,
        fullTimeEmployees: member.fullTimeEmployees,
        partTimeEmployees: member.partTimeEmployees,
        renewalDateUtcMilli: member.renewalDateUtcMilli,
        categoryId: member.categoryId,
      })),
    [membersData?.getCmpManageMembers.rows]
  );
  const isInitialLookupLoading =
    !chamberId ||
    (detailLoading && !backendEmail) ||
    (!hasLoadedEmail && (settingsLoading || !currentEmailSettings));

  const refreshEmailDetailAfterMutation = async () => {
    try {
      await refetchEmailDetail();
    } catch {
      // The confirmed mutation result is authoritative. A cache refresh error
      // must not report a successful action as a failure or prompt a retry.
    }
  };
  const deliveryActions = useNewsletterDeliveryActions({
    chamberId,
    timeZone,
    getNewsletterId: () => emailId,
    afterMutation: refreshEmailDetailAfterMutation,
  });

  const submitEmail = async (email: EmailFormState) => {
    if (!chamberId || !emailId) {
      throw new UserFacingError(applyCmpOrganizationNoun(EMAIL_CHAMBER_LOADING_MESSAGE, noun));
    }

    const result = await updateCmpNewsletter({
      variables: {
        input: {
          chamberId,
          newsletterId: emailId,
          data: emailFormStateToInput({ ...email, timeZone: timeZone || "" }, chamber),
        },
      },
    });
    const updateResult = result.data?.updateCmpNewsletter;
    const savedEmail = updateResult?.newsletter;

    if (
      updateResult?.newsletterSavedSuccessfully !== true
      || !savedEmail
    ) {
      throw new Error("The email draft could not be updated.");
    }

    const savedFormEmail = newsletterReturnToFormState(savedEmail, timeZone || "");
    assertEmailBuilderDraftPersisted({
      submittedEmail: email,
      savedEmail: savedFormEmail,
    });
    await refreshEmailDetailAfterMutation();

    return savedFormEmail;
  };

  const archiveRecipientGroup = async (groupId: string) => {
    if (!chamberId) {
      throw new UserFacingError(
        applyCmpOrganizationNoun(EMAIL_CHAMBER_LOADING_MESSAGE, noun)
      );
    }

    const result = await archiveCmpNewsletterRecipientGroup({
      variables: { input: { chamberId, groupId } },
    });
    if (!result.data?.archiveCmpNewsletterRecipientGroup?.groupSavedSuccessfully) {
      throw new Error("The custom audience list could not be archived.");
    }
    // Apollo applies the returned group's isArchived value to the cached list.
    // A refresh failure must not report a confirmed archive as a failure.
    await Promise.allSettled([refetchGroups()]);
  };

  const previewRecipients = async (email: EmailFormState) => {
    if (!chamberId) {
      throw new UserFacingError(
        applyCmpOrganizationNoun(EMAIL_CHAMBER_LOADING_MESSAGE, noun)
      );
    }

    const result = await previewCmpNewsletterRecipients({
      variables: {
        input: {
          chamberId,
          recipientSelection: emailFormStateToInput({ ...email, timeZone: timeZone || "" }, chamber)
            .recipientSelection!,
        },
      },
    });

    if (result.error) throw result.error;
    if (result.errors?.length) throw new ApolloError({ graphQLErrors: result.errors });
    if (!result.data?.previewCmpNewsletterRecipients) {
      throw new Error("The recipient preview could not be loaded.");
    }
    return previewQueryToSummary(result.data.previewCmpNewsletterRecipients);
  };

  const duplicateAsDraft = async (email: EmailFormState) => {
    if (!chamberId) {
      throw new UserFacingError(
        applyCmpOrganizationNoun(EMAIL_CHAMBER_LOADING_MESSAGE, noun)
      );
    }

    const duplicatedEmail = buildEmailDuplicateDraft(email);
    const result = await createCmpNewsletter({
      variables: {
        input: {
          chamberId,
          ...(chamber?.isSandbox && duplicatedEmail.isTestData
            ? { isCmpNewsletterModuleTestData: true }
            : {}),
          data: {
            ...emailFormStateToInput(duplicatedEmail, chamber),
            status: CmpNewsletterStatusEnum.Draft,
          },
        },
      },
    });
    const createResult = result.data?.createCmpNewsletter;
    const savedEmail = createResult?.newsletter;

    if (
      createResult?.newsletterSavedSuccessfully !== true
      || !savedEmail?.id
    ) {
      throw new Error("The email could not be duplicated.");
    }

    navigate(
      RoutePaths.chamberAdmin_emails_update.replace(":emailId", savedEmail.id)
    );
  };

  const publishNewsroom = async (email: EmailFormState) => {
    if (!chamberId || !emailId) {
      throw new UserFacingError(applyCmpOrganizationNoun(EMAIL_CHAMBER_LOADING_MESSAGE, noun));
    }

    const result = await publishCmpNewsletterToNewsroom({
      variables: { input: { chamberId, newsletterId: emailId } },
    });
    const publishResult = result.data?.publishCmpNewsletterToNewsroom;
    const savedEmail = publishResult?.newsletter;

    if (
      publishResult?.newsletterSavedSuccessfully !== true
      || !savedEmail
    ) {
      throw new Error("The newsroom record could not be published.");
    }

    await refreshEmailDetailAfterMutation();

    return newsletterReturnToFormState(savedEmail, timeZone || "");
  };

  // F2 / design (16) "Update Published Email": only the newsroom article of
  // a Sent email changes; the sent email record stays as sent.
  const updatePublishedNewsroom = async (email: EmailFormState) => {
    if (!chamberId || !emailId) {
      throw new UserFacingError(applyCmpOrganizationNoun(EMAIL_CHAMBER_LOADING_MESSAGE, noun));
    }
    const newsroom = emailFormStateToInput({ ...email, timeZone: timeZone || "" }, chamber).newsroom;
    const result = await updateCmpNewsletterNewsroom({
      variables: { input: {
        chamberId,
        newsletterId: emailId,
        title: newsroom?.title,
        summary: newsroom?.summary,
        body: newsroom?.body,
        featuredImageUrl: email.newsroomImageUrl.trim(),
      } },
    });
    const updateResult = result.data?.updateCmpNewsletterNewsroom;
    if (updateResult?.newsletterSavedSuccessfully !== true || !updateResult.newsletter) {
      throw new UserFacingError(
        buildStaffErrorFallback("save your changes to the newsroom article")
      );
    }
    await refreshEmailDetailAfterMutation();
    return newsletterReturnToFormState(updateResult.newsletter, timeZone || "");
  };

  const unpublishNewsroom = async (email: EmailFormState) => {
    const savedEmail = await unpublishFromNewsroom(emailId);

    await refreshEmailDetailAfterMutation();

    return newsletterReturnToFormState(savedEmail, timeZone || "");
  };

  const sendNow = async (email: EmailFormState) => {
    if (!chamberId || !emailId) {
      throw new UserFacingError(applyCmpOrganizationNoun(EMAIL_CHAMBER_LOADING_MESSAGE, noun));
    }

    const result = await sendCmpNewsletterNow({
      variables: {
        input: {
          chamberId,
          newsletterId: emailId,
          note: email.reviewNote || undefined,
        },
      },
    });
    const sendResult = result.data?.sendCmpNewsletterNow;
    const savedEmail = sendResult?.newsletter;

    if (!sendResult?.newsletterSendQueuedSuccessfully || !savedEmail) {
      throw new UserFacingError(
        "We could not confirm that sending started. Open Manage Emails & Communications and check the status of this email before you try again."
      );
    }

    return newsletterReturnToFormState(savedEmail, timeZone || "");
  };

  const isInitialEmailNotFound =
    !backendEmail && isNewsletterNotFoundError(detailError);
  const missingScheduleTimeZone = needsScheduleTimeZone && !timeZone && !timeZoneLoading;
  const lookupErrors = [
    { error: timeZoneError || (missingScheduleTimeZone ? new Error("Missing chamber time zone") : undefined), message: emailLoadErrorText(`your ${noun.singular} time zone`), retry: refetchTimeZone },
    { error: settingsError, message: emailLoadErrorText(`your ${noun.singular} email settings`), retry: refetchSettings },
    { error: isInitialEmailNotFound ? undefined : detailError, message: emailLoadErrorText("this email"), retry: refetchEmailDetail },
    { error: groupsError, message: emailLoadErrorText("your custom audiences"), retry: refetchGroups },
    { error: membersError, message: emailLoadErrorText("your member list"), retry: refetchMembers },
  ].filter(({ error }) => error);
  const lookupErrorNotices = lookupErrors.map(({ message, retry }) => (
    <div
      key={message}
      className="mb-4 rounded-md border border-red-200 bg-red-50 px-5 py-4 text-sm font-semibold text-red-700"
      role="alert"
    >
      {message}
      <button type="button" className="ml-3 min-h-[44px] px-2 underline" onClick={() => { void retry().catch(() => undefined); }}>Try again</button>
    </div>
  ));

  if (
    ((settingsError || (needsScheduleTimeZone && (timeZoneError || missingScheduleTimeZone))) && !hasLoadedEmail) ||
    (detailError && !backendEmail && !isInitialEmailNotFound)
  ) {
    return <>{lookupErrorNotices}</>;
  }

  if (isInitialEmailNotFound || (!detailLoading && !backendEmail)) {
    return (
      <div className="mx-auto max-w-3xl rounded-lg border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-900">
        <p className="font-bold">We could not find this email.</p>
        <p className="mt-1">
          It may have been deleted. Select Back to Manage Emails and choose
          the email again.
        </p>
        <button
          type="button"
          onClick={() => navigate(RoutePaths.chamberAdmin_emails_manage)}
          className="mt-4 rounded-lg border border-cpaGold bg-cpaGold px-4 py-2 text-sm font-bold text-white transition hover:bg-amber-500"
        >
          Back to Manage Emails
        </button>
      </div>
    );
  }

  if (isInitialLookupLoading || (!hasLoadedEmail && needsScheduleTimeZone && !timeZone)) {
    return (
      <div
        className="rounded-md border border-gray-200 bg-white px-5 py-4 text-sm font-semibold text-gray-600"
        role="status"
      >
        Loading your email...
      </div>
    );
  }

  return (
    <>
      {lookupErrorNotices}
      <EmailForm
        key={emailIdentity}
        mode="update"
        initialStep={Number(searchParams.get("step"))}
        showDraftSavedOnLoad={
          (location.state as { draftSaved?: boolean } | null)?.draftSaved === true
        }
        timeZone={timeZone}
        initialEmail={initialEmail}
        recipientGroups={recipientGroups}
        memberOptions={memberOptions}
        memberOptionsLoading={membersLoading}
        senderEmailOptions={senderEmailOptions}
        isLoading={false}
        isSaving={
          updatingNewsletter ||
          sendingNow ||
          deliveryActions.isRunning
        }
        onSubmit={submitEmail}
        onArchiveRecipientGroup={archiveRecipientGroup}
        onPreviewRecipients={previewRecipients}
        onDuplicateAsDraft={duplicateAsDraft}
        onPublishNewsroom={publishNewsroom}
        onUnpublishNewsroom={unpublishNewsroom}
        onUpdatePublishedNewsroom={updatePublishedNewsroom}
        onPreviewTestSend={deliveryActions.previewTestSend}
        onSendReview={deliveryActions.sendReview}
        onSendNow={sendNow}
        onSendSuccess={handleSendSuccess}
        onSchedule={deliveryActions.scheduleEmail}
        onCancelSchedule={deliveryActions.cancelSchedule}
      />
    </>
  );
};

export default UpdateEmail;
